/**
 * Mobile Drawer
 *
 * Left-side navigation drawer for mobile. Slides in from the left
 * (hamburger menu is on the left side of the top bar). Contains view navigation,
 * settings, insights, profile, and logout.
 * Only visible below the `md` breakpoint (< 768px).
 */

import { X, Settings, BarChart3, LogOut, Users, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { getViewIconComponent } from '../utils/viewIcons';
import type { View } from '../types';
import { useState, useCallback } from 'react';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  views: View[];
  currentView: string;
  onViewChange: (viewSlug: string) => void;
  onSettingsClick: () => void;
  onInsightsClick: () => void;
}

export function MobileDrawer({
  isOpen,
  onClose,
  views,
  currentView,
  onViewChange,
  onSettingsClick,
  onInsightsClick,
}: MobileDrawerProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [avatarFailed, setAvatarFailed] = useState(false);
  const handleAvatarError = useCallback(() => setAvatarFailed(true), []);

  const handleLogout = async () => {
    onClose();
    navigate('/');
    await logout();
  };

  const getInitials = () => {
    if (user?.name) {
      const parts = user.name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return user?.email?.substring(0, 2).toUpperCase() ?? '??';
  };

  if (!isOpen) return null;

  return (
    <div className="md:hidden fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 transition-opacity"
        onClick={onClose}
      />
      {/* Drawer panel -- right side */}
      <div className="absolute top-0 left-0 bottom-0 w-[80%] max-w-xs bg-surface-raised-light dark:bg-surface-raised-dark transform transition-transform duration-300 safe-area-top safe-area-bottom flex flex-col">
        {/* Header: user info + close */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border-subtle-light dark:border-border-subtle-dark shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {/* User avatar */}
            {user?.avatarUrl && !avatarFailed ? (
              <img
                src={user.avatarUrl}
                alt={user.name || user.email}
                className="w-8 h-8 rounded-full object-cover ring-1 ring-primary-500/40 shrink-0"
                onError={handleAvatarError}
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary-700 dark:bg-primary-800 flex items-center justify-center text-white text-xs font-bold tracking-wide ring-1 ring-primary-500/40 shrink-0">
                {getInitials()}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-mono text-sm font-medium text-text-primary-light dark:text-text-primary-dark truncate">
                {user?.name || 'User'}
              </div>
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark truncate">
                {user?.email}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-1 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-text-secondary-light dark:text-text-secondary-dark shrink-0"
            aria-label="Close drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* View list */}
        <nav className="flex-1 overflow-y-auto py-2">
          {views.map((view) => {
            const ViewIcon = getViewIconComponent(view);
            const isActive = view.slug === currentView;
            return (
              <button
                key={view.slug}
                onClick={() => {
                  onViewChange(view.slug);
                  onClose();
                }}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                  isActive
                    ? 'bg-primary-900/20 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                    : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark'
                )}
              >
                <ViewIcon
                  className={clsx(
                    'w-5 h-5 shrink-0',
                    isActive
                      ? 'text-primary-700 dark:text-primary-500'
                      : 'text-text-tertiary-light dark:text-text-tertiary-dark'
                  )}
                />
                <span className="font-mono text-sm">{view.name}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom: completed, shared lists, settings, insights, profile, logout */}
        <div className="border-t border-border-subtle-light dark:border-border-subtle-dark px-4 py-3 space-y-1">
          <button
            onClick={() => {
              onViewChange('completed');
              onClose();
            }}
            className={clsx(
              'w-full flex items-center gap-3 px-2 py-2.5 rounded-lg transition-colors font-mono text-sm',
              currentView === 'completed'
                ? 'bg-primary-900/20 text-primary-800 dark:text-primary-400'
                : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark'
            )}
          >
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>Completed</span>
          </button>
          <button
            onClick={() => {
              onClose();
              navigate('/app/shared-lists');
            }}
            className="w-full flex items-center gap-3 px-2 py-2.5 text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors"
          >
            <Users className="w-4 h-4 shrink-0" />
            <span className="font-mono text-sm">Shared Lists</span>
          </button>
          <button
            onClick={() => {
              onSettingsClick();
              onClose();
            }}
            className="w-full flex items-center gap-3 px-2 py-2.5 text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4 shrink-0" />
            <span className="font-mono text-sm">Settings</span>
          </button>
          <button
            onClick={() => {
              onInsightsClick();
              onClose();
            }}
            className="w-full flex items-center gap-3 px-2 py-2.5 text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors"
          >
            <BarChart3 className="w-4 h-4 shrink-0" />
            <span className="font-mono text-sm">Insights</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-2 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            <span className="font-mono text-sm">Logout</span>
          </button>
          <p className="mt-3 text-center font-mono text-xs text-text-tertiary-light dark:text-text-tertiary-dark opacity-40">
            v{import.meta.env.VITE_APP_VERSION || '0.0.0'}
          </p>
        </div>
      </div>
    </div>
  );
}
